import * as React from "react";
import { MenuIcon } from "lucide-react";
import { cn } from "../utils/cn";
import { Button, CommandPalette, CommandPaletteProvider, Sheet, SheetContent, SheetTrigger, Toaster } from "../components/ui";
import { MobileBottomNav, type MobileBottomNavItem } from "./MobileBottomNav";
import { Topbar } from "./Topbar";

export type ThemeMode = "light" | "dark" | "system";

export interface ThemeContextValue {
  theme: ThemeMode;
  resolvedTheme: "light" | "dark";
  setTheme: (theme: ThemeMode) => void;
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null);

function getSystemTheme(): "light" | "dark" {
  if (typeof window === "undefined") {
    return "light";
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function getStoredTheme(defaultTheme: ThemeMode): ThemeMode {
  if (typeof window === "undefined") {
    return defaultTheme;
  }
  const stored = window.localStorage.getItem("stenpro-theme");
  return stored === "light" || stored === "dark" || stored === "system" ? stored : defaultTheme;
}

export interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: ThemeMode;
  storageKey?: string;
}

export function ThemeProvider({ children, defaultTheme = "system", storageKey = "stenpro-theme" }: ThemeProviderProps) {
  const [theme, setThemeState] = React.useState<ThemeMode>(() => getStoredTheme(defaultTheme));
  const [systemTheme, setSystemTheme] = React.useState<"light" | "dark">(() => getSystemTheme());
  const resolvedTheme = theme === "system" ? systemTheme : theme;

  React.useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = () => setSystemTheme(media.matches ? "dark" : "light");
    handleChange();
    media.addEventListener("change", handleChange);
    return () => media.removeEventListener("change", handleChange);
  }, []);

  React.useEffect(() => {
    document.documentElement.dataset.theme = resolvedTheme;
    document.documentElement.classList.toggle("dark", resolvedTheme === "dark");
  }, [resolvedTheme]);

  const setTheme = React.useCallback(
    (nextTheme: ThemeMode) => {
      window.localStorage.setItem(storageKey, nextTheme);
      setThemeState(nextTheme);
    },
    [storageKey],
  );

  const value = React.useMemo(() => ({ theme, resolvedTheme, setTheme }), [resolvedTheme, setTheme, theme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
}

export interface AppShellProps {
  appId?: string;
  appSwitcher?: boolean | { hidden?: boolean };
  sidebar: React.ReactNode;
  topbar?: React.ReactNode;
  children: React.ReactNode;
  theme?: ThemeMode;
  className?: string;
  contentClassName?: string;
  mobileSidebarLabel?: string;
  mobileBottomNavItems?: MobileBottomNavItem[];
  mobileBottomNavActiveId?: string;
  mobileBottomNavLabel?: string;
}

function AppShellContent({
  appId,
  appSwitcher,
  sidebar,
  topbar,
  children,
  className,
  contentClassName,
  mobileSidebarLabel = "Öppna meny",
  mobileBottomNavItems = [],
  mobileBottomNavActiveId,
  mobileBottomNavLabel,
}: Omit<AppShellProps, "theme">) {
  const hasMobileBottomNav = mobileBottomNavItems.length > 0;
  const mobileNavigation = (
    <Sheet>
      <SheetTrigger asChild>
        <Button className="touch-target lg:hidden" size="icon" variant="ghost" aria-label={mobileSidebarLabel}>
          <MenuIcon className="size-5" />
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="h-[100dvh] w-80 max-w-[85vw] p-0 pb-safe-bottom pt-safe-top">
        {sidebar}
      </SheetContent>
    </Sheet>
  );
  const topbarIsTopbarElement = React.isValidElement<React.ComponentProps<typeof Topbar>>(topbar) && topbar.type === Topbar;
  const renderedTopbar = topbarIsTopbarElement
    ? React.cloneElement(topbar, {
        navigationSlot: topbar.props.navigationSlot ?? mobileNavigation,
      })
    : topbar;

  return (
    <CommandPaletteProvider>
      <div className={cn("min-h-[100dvh] bg-background text-foreground", className)}>
        {topbar && !topbarIsTopbarElement ? (
          <div className="fixed left-2 top-[calc(var(--safe-top)+0.25rem)] z-40 lg:hidden">{mobileNavigation}</div>
        ) : null}
        <div className="flex min-h-[100dvh] flex-col">
          <div className="flex min-h-0 flex-1">
            <aside className="hidden shrink-0 lg:block">{sidebar}</aside>
            <div className="flex min-w-0 flex-1 flex-col">
              {renderedTopbar ?? <Topbar appId={appId} appSwitcher={appSwitcher} navigationSlot={mobileNavigation} />}
              <main
                className={cn(
                  "min-w-0 flex-1 px-4 py-5 pb-[calc(var(--safe-bottom)+1.25rem)] sm:px-6 sm:py-6 lg:px-8",
                  hasMobileBottomNav && "pb-[calc(var(--safe-bottom)+5.5rem)] lg:pb-6",
                  contentClassName,
                )}
              >
                {children}
              </main>
            </div>
          </div>
        </div>
        <MobileBottomNav items={mobileBottomNavItems} activeId={mobileBottomNavActiveId} label={mobileBottomNavLabel} />
        <CommandPalette />
        <Toaster
          position="top-right"
          richColors
          closeButton
          expand={false}
          visibleToasts={3}
          style={{ maxHeight: "18rem", overflow: "hidden" }}
        />
      </div>
    </CommandPaletteProvider>
  );
}

export function AppShell({ theme = "system", ...props }: AppShellProps) {
  return (
    <ThemeProvider defaultTheme={theme}>
      <AppShellContent {...props} />
    </ThemeProvider>
  );
}
